/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { TranscriptionBlock } from "types/Block";
import { Profile } from "types/Profile";

export const getUnknownSpeakerName = (): string =>
  I18n.t("project_show.unknown_speaker_name");

const getUnknownSpeakerNameForBlock = (block: TranscriptionBlock): string => {
  if (block.unidentifiedSpeakerNumber >= 0) {
    return block.unidentifiedSpeakerNumber.toString();
  } else {
    return getUnknownSpeakerName();
  }
};

/**
 *
 * `profile` is `data` from `behanceApi.endpoints.getProfileByAdobeId.select(userId)`
 * `displayName` stores the chosen name for the anonymous participant
 * It historically stored a copy of the behance profile set at recording time (we not longer do it but old projects will have it)
 * for Adobe users we read client side from behance to get the `displayName`
 * However we still need to work out _which_ display name to use, depending on the type of user.
 */
export function getDisplayNameForBlock(
  block: TranscriptionBlock,
  profile: Profile | null | undefined
): string {
  let defaultedDisplayName: string | undefined;

  if (block.isAnonymous || block.displayNameIsOverridden) {
    defaultedDisplayName = block?.displayName ?? profile?.displayName;
  } else {
    defaultedDisplayName = profile?.displayName ?? block?.displayName;
  }

  return defaultedDisplayName ?? getUnknownSpeakerNameForBlock(block);
}
